// Global delete confirmation store module
// Manages delete confirmation dialogs across the app

const state = {
  // Dialog visibility
  isVisible: false,

  // Content being deleted
  deleteItem: null, // The item(s) to delete
  deleteType: 'content', // 'content', 'upload', 'collection', etc.

  // Display information
  contentCount: 0,
  contentType: 'items', // 'image', 'video', 'items', etc.

  // State management
  isDeleting: false,

  // Callback functions
  onConfirm: null, // Function to call when delete is confirmed
  onCancel: null // Function to call when delete is cancelled
};

const getters = {
  isVisible: state => state.isVisible,
  deleteItem: state => state.deleteItem,
  deleteType: state => state.deleteType,
  contentCount: state => state.contentCount,
  contentType: state => state.contentType,
  isDeleting: state => state.isDeleting
};

const mutations = {
  SET_VISIBLE(state, visible) {
    state.isVisible = visible;
  },

  SET_DELETE_ITEM(state, item) {
    state.deleteItem = item;
  },

  SET_DELETE_TYPE(state, type) {
    state.deleteType = type;
  },

  SET_CONTENT_COUNT(state, count) {
    state.contentCount = count;
  },

  SET_CONTENT_TYPE(state, type) {
    state.contentType = type;
  },

  SET_DELETING(state, isDeleting) {
    state.isDeleting = isDeleting;
  },

  SET_CALLBACKS(state, { onConfirm, onCancel }) {
    state.onConfirm = onConfirm;
    state.onCancel = onCancel;
  },

  RESET_STATE(state) {
    state.isVisible = false;
    state.deleteItem = null;
    state.deleteType = 'content';
    state.contentCount = 0;
    state.contentType = 'items';
    state.isDeleting = false;
    state.onConfirm = null;
    state.onCancel = null;
  }
};

const actions = {
  // Show delete confirmation dialog
  showDeleteConfirmation(
    { commit },
    {
      item, // The item(s) to delete
      type = 'content', // Type of deletion ('content', 'upload', etc.)
      contentCount = 1, // Number of items being deleted
      contentType = 'items', // Display type ('image', 'video', 'items')
      onConfirm, // Callback when user confirms
      onCancel // Callback when user cancels (optional)
    }
  ) {
    commit('SET_DELETE_ITEM', item);
    commit('SET_DELETE_TYPE', type);
    commit('SET_CONTENT_COUNT', contentCount);
    commit('SET_CONTENT_TYPE', contentType);
    commit('SET_CALLBACKS', { onConfirm, onCancel });
    commit('SET_VISIBLE', true);
  },

  // Hide the dialog
  hideDeleteConfirmation({ commit }) {
    commit('SET_VISIBLE', false);
    // Don't reset state immediately to allow for smooth closing animation
    setTimeout(() => {
      commit('RESET_STATE');
    }, 300);
  },

  // Set deleting state
  setDeleting({ commit }, isDeleting) {
    commit('SET_DELETING', isDeleting);
  },

  // Confirm deletion
  async confirmDelete({ state, commit, dispatch }) {
    if (!state.onConfirm) return;

    commit('SET_DELETING', true);

    try {
      // Handle different deletion types
      if (state.deleteType === 'version') {
        // Version deletion uses removeImageFromStack API
        await dispatch('handleVersionDeletion');
      } else {
        // Regular deletion (content, upload, etc.)
        await state.onConfirm(state.deleteItem);
      }

      dispatch('hideDeleteConfirmation');
    } catch (error) {
      console.error('Delete confirmation error:', error);
    } finally {
      commit('SET_DELETING', false);
    }
  },

  // Handle version deletion specifically
  async handleVersionDeletion({ state }) {
    const { ContentApi } = await import('@/api/modules/content');

    if (!state.deleteItem || !state.deleteItem.id) {
      throw new Error('No version to delete');
    }

    // Use the version-specific API
    await ContentApi.removeContentFromStack({ id: state.deleteItem.id, type: state.deleteItem.type });

    // Call the onConfirm callback if provided (for post-deletion actions)
    if (state.onConfirm) {
      await state.onConfirm(state.deleteItem);
    }
  },

  // Cancel deletion
  cancelDelete({ state, dispatch }) {
    if (state.onCancel) {
      state.onCancel();
    }
    dispatch('hideDeleteConfirmation');
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
